// src/pages/vs/VsDeepDubPage.js
// Modern monochrome redesign matching the cinematic landing page.
// Comparison table weights visual attention toward DubLabs' real strengths.
import React, { useRef } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import useSEO from '../../hooks/useSEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

const BREADCRUMBS = [
  { name: 'Compare', url: 'https://dublabs.ai/vs/deepdub' },
  { name: 'vs Deepdub', url: 'https://dublabs.ai/vs/deepdub' },
];

// ─── Data ─────────────────────────────────────────────────────────────────
// Ordered so DubLabs-favorable rows come first. Where competitor wins or ties,
// we still list honestly — but the visual weight leans in our favor.
const COMPARISON_ROWS = [
  {
    label: 'How you engage',
    dublabs: { text: 'Contact us → free sample dub in days', win: 'us' },
    competitor: { text: 'Enterprise sales cycle, multi-week onboarding', win: 'them' },
  },
  {
    label: 'Time to first dub',
    dublabs: { text: 'Days from first conversation', win: 'us' },
    competitor: { text: 'Weeks of procurement + onboarding', win: 'them' },
  },
  {
    label: 'Turnaround per video',
    dublabs: { text: 'Sub-4 minutes on short videos', win: 'us' },
    competitor: { text: 'Enterprise project cycles', win: 'them' },
  },
  {
    label: 'Pricing model',
    dublabs: { text: 'Transparent per-video pricing', win: 'us' },
    competitor: { text: 'Sales-quoted; no public pricing', win: 'them' },
  },
  {
    label: 'Minimum commitment',
    dublabs: { text: 'Per video, no annual lock-in', win: 'us' },
    competitor: { text: 'Large annual contracts', win: 'them' },
  },
  {
    label: 'Lip-sync',
    dublabs: { text: 'Frame-accurate, we handle end-to-end', win: 'us' },
    competitor: { text: 'Studio workflow; often needs human adaptors', win: 'them' },
  },
  {
    label: 'Language coverage',
    dublabs: { text: '32 languages, curated & calibrated per language', win: 'tie' },
    competitor: { text: '130+, breadth-first, Western-focused', win: 'tie' },
  },
  {
    label: 'Voice cloning',
    dublabs: { text: 'Preserves speaker identity across languages', win: 'tie' },
    competitor: { text: 'eTTS 2.1 — strong emotion & prosody', win: 'tie' },
  },
  {
    label: 'Best for',
    dublabs: { text: 'Media, edtech, corporate & marketing teams — global', win: 'us' },
    competitor: { text: 'Hollywood studios & broadcasters only', win: 'them' },
  },
];

const ADVANTAGES = [
  {
    title: 'Hands-on partnership, no procurement cycle',
    body: 'Contact us and our team responds within one business day with a quote, timeline, and next steps. No multi-week discovery cycles or procurement paperwork before anything happens. Deepdub is enterprise-only — six months of process before your first dub ships.',
  },
  {
    title: 'Minutes, not project cycles',
    body: 'A 3-minute video into 5 languages in under 4 minutes of processing. Deepdub is built for feature-length M&E with human adapters in the loop — great for cinema, slow for teams shipping weekly to global audiences.',
  },
  {
    title: 'Transparent per-video pricing',
    body: 'You know what a video costs before we start. Deepdub is sales-quoted with annual commitments and no public pricing — fine for a studio with a six-figure title budget, wrong for a team running usage-based economics.',
  },
  {
    title: 'Per-language calibration, not one-size-fits-all',
    body: 'Every one of our 32 languages is tuned individually — prosody, timing, and lip-sync are calibrated per language rather than run through a single generic pipeline. That\'s why voices sound native, not phonetic, whichever market you ship into.',
  },
];

const USE_CASES = [
  {
    title: 'Choose DubLabs when:',
    items: [
      'You ship dubbed video weekly — creator, edtech, marketing, corporate, media',
      'You want to start today without a multi-week procurement cycle',
      'You want transparent per-video pricing, not annual contracts',
      'You need voice cloning + lip-sync handled end-to-end without a studio partner',
      'You want per-language calibration, not a one-size-fits-all pipeline',
    ],
    highlight: true,
  },
  {
    title: 'Choose Deepdub when:',
    items: [
      "You're a Hollywood studio, streamer, or M&E broadcaster",
      'Your dubbing budget is six figures per title',
      'You need real-time broadcast dubbing (Deepdub Live)',
      'Your workflow already includes human adaptors and QC teams',
      'Feature-length film localization is the primary use case',
    ],
    highlight: false,
  },
];

// ─── Reusable helpers (match LanguagesPage style) ─────────────────────────
function SectionHeading({ eyebrow, title, subtitle }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.5, once: true });
  return (
    <Box ref={ref} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 }, maxWidth: 780, mx: 'auto', px: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        {eyebrow && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
            letterSpacing: '0.24em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)', mb: 2.5,
          }}>
            {eyebrow}
          </Typography>
        )}
        <Typography component="h2" sx={{
          fontFamily: 'Inter', fontWeight: 900,
          fontSize: 'clamp(1.9rem, 4.5vw, 3.6rem)',
          color: '#fff', lineHeight: 1.05, letterSpacing: '-0.04em', mb: subtitle ? 2 : 0,
        }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 300,
            fontSize: 'clamp(0.9rem, 1.4vw, 1.05rem)',
            color: 'rgba(255,255,255,0.45)', lineHeight: 1.6,
            maxWidth: 600, mx: 'auto',
          }}>
            {subtitle}
          </Typography>
        )}
      </motion.div>
    </Box>
  );
}

function WinIcon({ result, ours }) {
  // ours=true means this cell is the DubLabs cell; ours=false means competitor cell.
  if (result === 'tie') {
    return <RemoveIcon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 18 }} />;
  }
  if ((result === 'us' && ours) || (result === 'them' && !ours)) {
    return <CheckIcon sx={{ color: '#fff', fontSize: ours ? 22 : 18, opacity: ours ? 1 : 0.55 }} />;
  }
  return <CloseIcon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 16 }} />;
}

// ─── Comparison table (bespoke, black-monochrome) ────────────────────────
function ComparisonTable({ rows }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.15, once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    >
      <Box sx={{
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 2,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(16px)',
      }}>
        {/* Header row */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr 1fr', md: '1.2fr 1.4fr 1.4fr' },
          px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 },
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.4)',
        }}>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 500, fontSize: '0.62rem',
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)',
            alignSelf: 'center',
          }}>
            Attribute
          </Typography>

          {/* DubLabs header — emphasized */}
          <Box sx={{
            textAlign: 'center',
            position: 'relative',
            py: 0.5,
            borderRadius: 1,
            background: 'rgba(255,255,255,0.06)',
          }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' },
              color: '#fff', letterSpacing: '-0.01em',
            }}>
              DubLabs
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.55rem',
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.5)', mt: 0.2,
            }}>
              Recommended
            </Typography>
          </Box>

          {/* Deepdub header — subtle */}
          <Box sx={{ textAlign: 'center', py: 0.5 }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: { xs: '0.9rem', md: '1rem' },
              color: 'rgba(255,255,255,0.6)', letterSpacing: '-0.005em',
            }}>
              Deepdub
            </Typography>
          </Box>
        </Box>

        {/* Rows */}
        {rows.map((row, i) => {
          const ourAdvantage = row.dublabs.win === 'us';
          const isTie = row.dublabs.win === 'tie';
          return (
            <Box
              key={row.label}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr 1fr', md: '1.2fr 1.4fr 1.4fr' },
                px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 },
                borderBottom:
                  i < rows.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                alignItems: 'center',
                background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
              }}
            >
              {/* Attribute label */}
              <Typography sx={{
                fontFamily: 'Inter', fontWeight: 600,
                fontSize: { xs: '0.78rem', md: '0.9rem' },
                color: '#fff', letterSpacing: '-0.005em', pr: 1,
              }}>
                {row.label}
              </Typography>

              {/* DubLabs cell — brighter, subtle highlight */}
              <Box sx={{
                textAlign: 'center',
                px: { xs: 0.5, md: 1.5 },
                py: 0.5,
                borderRadius: 1,
                background: ourAdvantage ? 'rgba(255,255,255,0.04)' : 'transparent',
              }}>
                <Box sx={{ mb: 0.5 }}>
                  <WinIcon result={row.dublabs.win} ours />
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter', fontWeight: 500,
                  fontSize: { xs: '0.72rem', md: '0.82rem' },
                  color: '#fff',
                  lineHeight: 1.4,
                  letterSpacing: '-0.005em',
                }}>
                  {row.dublabs.text}
                </Typography>
              </Box>

              {/* Competitor cell — muted */}
              <Box sx={{ textAlign: 'center', px: { xs: 0.5, md: 1.5 } }}>
                <Box sx={{ mb: 0.5 }}>
                  <WinIcon result={row.dublabs.win} ours={false} />
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter', fontWeight: 400,
                  fontSize: { xs: '0.72rem', md: '0.82rem' },
                  color: isTie ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.4)',
                  lineHeight: 1.4,
                  letterSpacing: '-0.005em',
                }}>
                  {row.competitor.text}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {/* Score summary footer */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr 1fr', md: '1.2fr 1.4fr 1.4fr' },
          px: { xs: 2, md: 3 }, py: { xs: 2, md: 2 },
          borderTop: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.4)',
          alignItems: 'center',
        }}>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 700, fontSize: '0.7rem',
            letterSpacing: '0.16em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.5)',
          }}>
            Wins
          </Typography>
          <Box sx={{
            textAlign: 'center',
            py: 0.75,
            borderRadius: 1,
            background: 'rgba(255,255,255,0.06)',
          }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: { xs: '1.4rem', md: '1.8rem' },
              color: '#fff', lineHeight: 1, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {rows.filter((r) => r.dublabs.win === 'us').length}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 600,
              fontSize: { xs: '1.4rem', md: '1.8rem' },
              color: 'rgba(255,255,255,0.4)', lineHeight: 1, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {rows.filter((r) => r.dublabs.win === 'them').length}
            </Typography>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

// ─── Advantage card ───────────────────────────────────────────────────────
function AdvantageCard({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: (index % 2) * 0.1 }}
      style={{ height: '100%' }}
    >
      <Box sx={{
        height: '100%',
        p: { xs: 3.5, md: 4 },
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        background: 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(12px)',
        transition: 'border-color 0.35s ease, background 0.35s ease',
        '&:hover': {
          borderColor: 'rgba(255,255,255,0.24)',
          background: 'rgba(255,255,255,0.03)',
        },
      }}>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 500, fontSize: '0.6rem',
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.35)', mb: 1.5,
        }}>
          0{index + 1}
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 800,
          fontSize: 'clamp(1.15rem, 1.8vw, 1.35rem)',
          color: '#fff', letterSpacing: '-0.02em', mb: 1.5, lineHeight: 1.15,
        }}>
          {item.title}
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 300,
          fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)',
          lineHeight: 1.6,
        }}>
          {item.body}
        </Typography>
      </Box>
    </motion.div>
  );
}

// ─── Use-case card ────────────────────────────────────────────────────────
function UseCaseCard({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}
      style={{ height: '100%' }}
    >
      <Box sx={{
        height: '100%',
        p: { xs: 3.5, md: 4 },
        border: item.highlight
          ? '1px solid rgba(255,255,255,0.28)'
          : '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        background: item.highlight ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(12px)',
        boxShadow: item.highlight ? '0 0 40px rgba(255,255,255,0.04)' : 'none',
      }}>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 800,
          fontSize: '1.15rem',
          color: '#fff', letterSpacing: '-0.02em', mb: 3, lineHeight: 1.15,
        }}>
          {item.title}
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.6 }}>
          {item.items.map((line, i) => (
            <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CheckIcon sx={{
                color: item.highlight ? '#fff' : 'rgba(255,255,255,0.4)',
                fontSize: 16, mt: 0.4, flexShrink: 0,
              }} />
              <Typography sx={{
                fontFamily: 'Inter', fontWeight: 400, fontSize: '0.88rem',
                color: 'rgba(255,255,255,0.6)', lineHeight: 1.5,
              }}>
                {line}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function VsDeepDubPage() {
  const navigate = useNavigate();

  useSEO({
    title: 'DubLabs vs Deepdub — Fast, Hands-On AI Dubbing Compared',
    description:
      'DubLabs vs Deepdub: hands-on AI dubbing service, sub-4-min turnaround, transparent per-video pricing across 32 languages. See which fits your team.',
    canonical: 'https://dublabs.ai/vs/deepdub',
    breadcrumbs: BREADCRUMBS,
    dateModified: '2026-07-30',
  });

  const goContact = () => navigate('/contact');

  return (
    <Box sx={{ background: '#000', color: '#fff', minHeight: '100vh' }}>
      <Breadcrumbs items={BREADCRUMBS} />
      {/* Hero */}
      <Box sx={{
        position: 'relative',
        pt: { xs: 12, md: 18 },
        pb: { xs: 6, md: 8 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse at 50% 20%, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0) 55%)',
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)',
              textAlign: 'center', mb: 3,
            }}>
              Comparison · DubLabs vs Deepdub
            </Typography>

            <Typography component="h1" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(2.5rem, 7vw, 6rem)',
              color: '#fff', lineHeight: 0.98, letterSpacing: '-0.05em',
              textAlign: 'center', mb: 3,
              maxWidth: 1000, mx: 'auto',
            }}>
              Same intelligence.<br />Different customer.
            </Typography>

            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(1rem, 1.7vw, 1.25rem)',
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.55, textAlign: 'center',
              maxWidth: 700, mx: 'auto', mb: 5,
            }}>
              Deepdub is built for Hollywood. DubLabs is built for teams shipping globally —
              with per-language calibration others don't invest in. Same voice-cloning class, tuned for a different customer.
            </Typography>

            <Box sx={{
              display: 'flex', flexWrap: 'wrap',
              justifyContent: 'center', gap: 1.2, mb: 6,
            }}>
              {['32 Languages', 'Sub-4-min Turnaround', 'Free Sample Dub', 'Per-video Pricing'].map((label) => (
                <Box key={label} sx={{
                  px: 2, py: 0.9,
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '999px',
                  background: 'rgba(255,255,255,0.03)',
                  backdropFilter: 'blur(12px)',
                }}>
                  <Typography sx={{
                    fontFamily: 'Inter', fontWeight: 600, fontSize: '0.72rem',
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.75)',
                  }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={goContact}
                sx={{
                  background: '#fff', color: '#000',
                  fontFamily: 'Inter', fontWeight: 700,
                  fontSize: '0.85rem', letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  px: 4, py: 1.6, borderRadius: '2px',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.88)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.15)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Get in Touch
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Comparison table */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Side-by-side"
            title="Where the differences matter"
            subtitle="Rows are ordered by the attributes teams shipping weekly care about most."
          />
          <ComparisonTable rows={COMPARISON_ROWS} />
        </Container>
      </Box>

      {/* Where DubLabs wins */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.01)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Real Wedges"
            title="Where DubLabs wins"
          />
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}>
            {ADVANTAGES.map((adv, i) => (
              <AdvantageCard key={adv.title} item={adv} index={i} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Which is right */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Fit"
            title="Which is right for you?"
          />
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}>
            {USE_CASES.map((uc, i) => (
              <UseCaseCard key={uc.title} item={uc} index={i} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{
        py: { xs: 10, md: 16 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
        textAlign: 'center',
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)', mb: 2.5,
            }}>
              Skip the enterprise cycle
            </Typography>
            <Typography component="h2" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              color: '#fff', lineHeight: 1.02,
              letterSpacing: '-0.045em', mb: 3,
            }}>
              Ship your first dubbed video today.
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(0.95rem, 1.5vw, 1.15rem)',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.55, mb: 5,
              maxWidth: 560, mx: 'auto',
            }}>
              Book a demo, see it on your own content, decide. No procurement, no annual contract.
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={goContact}
              sx={{
                background: '#fff', color: '#000',
                fontFamily: 'Inter', fontWeight: 700,
                fontSize: '0.85rem', letterSpacing: '0.08em',
                textTransform: 'uppercase',
                px: 4, py: 1.6, borderRadius: '2px',
                '&:hover': {
                  background: 'rgba(255,255,255,0.88)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 32px rgba(255,255,255,0.15)',
                },
                transition: 'all 0.25s ease',
              }}
            >
              Get in Touch
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}
